import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, InputNumber, message, Select, Switch } from 'antd';
import { withTranslation } from 'react-i18next';
import {
  UserDetailHeadingCover,
  LineSeparator,
  LoaderContainer,
  InputContainer,
  InputLabel,
} from './styles';
import Loader from '../../components/Loader';
import { MANAGE_PRINT_DEFAULTS as trans } from './TranslationValues';
import { AppStoreContext } from '../../context/appStoreContext';
import {
  fetchOrgPrintCatalogMap,
  getOrgPrintDefaults,
  getOrganizationIdForPrintCatalog,
  printCatalogFieldToDimension,
  resetOrgPrintDefaults,
  sortPrintCatalogEntries,
  updateOrgPrintDefaults,
  PRINT_DEFAULTS_STATUS,
} from '../../utils/services/printDefaultsAPI';

const NAMESPACE = ['MANAGE_PRINT_DEFAULTS'];

const { Option } = Select;

const PrintDefaults = ({ t }) => {
  const { userDetails } = useContext(AppStoreContext);
  const organizationId = getOrganizationIdForPrintCatalog(userDetails);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [colorMode, setColorMode] = useState();
  const [paperWeight, setPaperWeight] = useState(80);
  const [mailType, setMailType] = useState();
  const [envelopeType, setEnvelopeType] = useState();
  const [returnEnvelope, setReturnEnvelope] = useState(false);
  const [catalogMap, setCatalogMap] = useState(null);

  const applyData = useCallback(data => {
    if (!data) return;
    setColorMode(data.colorMode);
    setPaperWeight(data.paperWeight ?? 80);
    setMailType(data.mailType);
    setEnvelopeType(data.envelopeType);
    setReturnEnvelope(data.returnEnvelope ?? false);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [defaultsRes, catalogData] = await Promise.all([
        getOrgPrintDefaults(),
        organizationId ? fetchOrgPrintCatalogMap(organizationId) : Promise.resolve({}),
      ]);
      if (defaultsRes && defaultsRes.statusCode === PRINT_DEFAULTS_STATUS.GET_SUCCESS && defaultsRes.data) {
        applyData(defaultsRes.data);
      } else {
        message.error(t(trans.FETCH_ERROR_MESSAGE.key, trans.FETCH_ERROR_MESSAGE.value));
      }
      setCatalogMap(catalogData && typeof catalogData === 'object' ? catalogData : {});
    } catch (e) {
      message.error(t(trans.FETCH_ERROR_MESSAGE.key, trans.FETCH_ERROR_MESSAGE.value));
      setCatalogMap({});
    } finally {
      setLoading(false);
    }
  }, [applyData, organizationId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const catalogEntriesFor = useCallback(
    field => {
      const dim = printCatalogFieldToDimension(field);
      if (!dim || !catalogMap) {
        return [];
      }
      return sortPrintCatalogEntries(catalogMap[dim] || []);
    },
    [catalogMap],
  );

  const buildCatalogSelect = (field, currentValue, onChange, minWidth) => {
    const entries = catalogEntriesFor(field);
    const inCatalog =
      currentValue != null &&
      String(currentValue) !== '' &&
      entries.some(e => String(e.value) === String(currentValue));
    const selectValue = inCatalog ? currentValue : undefined;
    const emptyPlaceholder = t(
      trans.CATALOG_EMPTY_SELECT_PLACEHOLDER.key,
      trans.CATALOG_EMPTY_SELECT_PLACEHOLDER.value,
    );

    return (
      <Select
        size="large"
        value={entries.length ? selectValue : undefined}
        placeholder={entries.length ? undefined : emptyPlaceholder}
        style={{ minWidth }}
        allowClear={!!entries.length}
        notFoundContent={null}
        onChange={v => onChange(v != null && v !== '' ? v : '')}
      >
        {entries.map(row => (
          <Option key={String(row.id != null ? row.id : row.value)} value={row.value}>
            {row.displayLabel != null && row.displayLabel !== '' ? row.displayLabel : row.value}
          </Option>
        ))}
      </Select>
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        colorMode,
        paperWeight,
        mailType,
        envelopeType,
        returnEnvelope,
      };
      const response = await updateOrgPrintDefaults(payload);
      if (response && response.statusCode === PRINT_DEFAULTS_STATUS.UPDATE_SUCCESS) {
        applyData(response.data);
        message.success(t(trans.SAVE_SUCCESS_MESSAGE.key, trans.SAVE_SUCCESS_MESSAGE.value));
      } else {
        message.error(t(trans.SAVE_ERROR_MESSAGE.key, trans.SAVE_ERROR_MESSAGE.value));
      }
    } catch (e) {
      message.error(t(trans.SAVE_ERROR_MESSAGE.key, trans.SAVE_ERROR_MESSAGE.value));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const response = await resetOrgPrintDefaults();
      if (response && response.statusCode === PRINT_DEFAULTS_STATUS.UPDATE_SUCCESS) {
        setColorMode(undefined);
        setMailType(undefined);
        setEnvelopeType(undefined);
        setPaperWeight(80);
        setReturnEnvelope(false);
        message.success(t(trans.RESET_SUCCESS_MESSAGE.key, trans.RESET_SUCCESS_MESSAGE.value));
      } else {
        message.error(t(trans.SAVE_ERROR_MESSAGE.key, trans.SAVE_ERROR_MESSAGE.value));
      }
    } catch (e) {
      message.error(t(trans.SAVE_ERROR_MESSAGE.key, trans.SAVE_ERROR_MESSAGE.value));
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (labelKey, helpKey, control) => (
    <InputContainer full style={{ marginBottom: '18px', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <div style={{ flex: '1 1 280px', maxWidth: '520px' }}>
          <InputLabel googleConfig style={{ marginLeft: 0 }}>
            {t(labelKey.key, labelKey.value)}
          </InputLabel>
          <p
            style={{
              margin: '4px 0 0 0',
              fontSize: '13px',
              color: 'rgba(30, 41, 51, 0.65)',
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            {t(helpKey.key, helpKey.value)}
          </p>
        </div>
        <div style={{ flex: '0 1 260px', display: 'flex', justifyContent: 'flex-end' }}>{control}</div>
      </div>
    </InputContainer>
  );

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <UserDetailHeadingCover separator>
        <div>
          <h5>{t(trans.TITLE.key, trans.TITLE.value)}</h5>
          <p>{t(trans.DESCRIPTION.key, trans.DESCRIPTION.value)}</p>
        </div>
      </UserDetailHeadingCover>
      <LineSeparator />
      <div style={{ maxWidth: '900px' }}>
        {renderRow(
          trans.COLOR_MODE_LABEL,
          trans.COLOR_MODE_HELP,
          buildCatalogSelect('colorMode', colorMode, setColorMode, 220),
        )}
        {renderRow(
          trans.PAPER_WEIGHT_LABEL,
          trans.PAPER_WEIGHT_HELP,
          <InputNumber
            size="large"
            min={1}
            max={500}
            value={paperWeight}
            onChange={v => setPaperWeight(v != null ? v : 80)}
            formatter={value => (value != null ? `${value} g/m²` : '')}
            parser={value => value.replace(/\s*g\/m²\s*/g, '')}
            style={{ width: 220 }}
          />,
        )}
        {renderRow(
          trans.MAIL_TYPE_LABEL,
          trans.MAIL_TYPE_HELP,
          buildCatalogSelect('mailType', mailType, setMailType, 220),
        )}
        {renderRow(
          trans.ENVELOPE_TYPE_LABEL,
          trans.ENVELOPE_TYPE_HELP,
          buildCatalogSelect('envelopeType', envelopeType, setEnvelopeType, 220),
        )}
        {renderRow(
          trans.RETURN_ENVELOPE_LABEL,
          trans.RETURN_ENVELOPE_HELP,
          <Switch
            className="sso-switch"
            checked={returnEnvelope}
            onChange={setReturnEnvelope}
            checkedChildren={t(trans.RETURN_ENVELOPE_YES.key, trans.RETURN_ENVELOPE_YES.value)}
            unCheckedChildren={t(trans.RETURN_ENVELOPE_NO.key, trans.RETURN_ENVELOPE_NO.value)}
          />,
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '28px',
            paddingTop: '20px',
            borderTop: '1px solid rgb(239, 241, 244)',
          }}
        >
          <Button size="large" onClick={handleReset} disabled={saving} icon="reload">
            {t(trans.RESET_BUTTON.key, trans.RESET_BUTTON.value)}
          </Button>
          <Button
            type="primary"
            className="add_user_btn"
            size="large"
            onClick={handleSave}
            loading={saving}
            icon="save"
          >
            {t(trans.SAVE_BUTTON.key, trans.SAVE_BUTTON.value)}
          </Button>
        </div>
      </div>
    </>
  );
};

export default withTranslation(NAMESPACE)(PrintDefaults);
